import { X, Shield, ExternalLink } from 'lucide-react';

interface TermsOfServiceProps {
  isOpen: boolean;
  onClose: () => void;
}

export const TermsOfService = ({ isOpen, onClose }: TermsOfServiceProps) => {
  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="terms-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl my-8"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — 2026 theme */}
        <div className="text-white px-6 py-4 border-b border-[#0f172a]/20 flex justify-between items-center sticky top-0 z-10" style={{ backgroundColor: 'var(--smb-primary, #1e3a5f)' }}>
          <div className="flex items-center gap-2">
            <Shield size={24} />
            <h2 
              id="terms-title"
              className="font-bold text-xl"
            >
              Terms of Service
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10"
            aria-label="Close Terms of Service"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 text-sm text-gray-700">
          {/* Introduction */}
          <section>
            <h3 className="font-bold text-gray-900 text-base mb-2">1. Purpose and Scope</h3>
            <p className="mb-2">
              <strong>VerifyPH</strong> ("we", "our", "the platform") is a citizen-powered transparency 
              platform that enables citizens to document and share observations related to government-funded 
              projects and public service delivery through evidence-based reporting.
            </p>
            <p className="mb-2">
              <strong>Core Principle:</strong> VerifyPH documents citizen-submitted evidence. It does not 
              make legal judgments, accusations, or official determinations.
            </p>
            <p className="mb-2">
              The platform supports multiple reporting categories, including <strong>Anti-Epal Watch</strong>, 
              which focuses on documenting observed promotional materials on government-funded projects in 
              relation to{' '}
              <a 
                href="https://dilg.gov.ph/news/ANTI-EPAL-DRIVE-DILG-Orders-Immediate-Removal-of-Officials-Names-and-Images-from-Government-Projects/NC-2026-1047"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 underline inline-flex items-center gap-1"
              >
                DILG Memorandum NC-2026-1047
                <ExternalLink size={14} />
              </a>.
            </p>
            <p>
              All entries are citizen-submitted observations displayed for transparency purposes only.
            </p>
          </section>

          {/* Acceptable Use */}
          <section>
            <h3 className="font-bold text-gray-900 text-base mb-2">2. Acceptable Citizen Reports</h3>
            <p className="mb-2">Citizen reports submitted to this platform must:</p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Document observations related to <strong>government-funded projects, programs, activities, or public services</strong></li>
              <li>Include clear photographic evidence supporting the observation</li>
              <li>Be factual, truthful, and accurate to the best of your knowledge</li>
              <li>Select the appropriate report category (Anti-Epal Watch, Project Delay, Ghost Project, Service Failure, or Compliance Gap)</li>
              <li>Provide all required information based on the selected category</li>
              <li>Not contain defamatory, false, or malicious content</li>
              <li>Not violate any applicable laws or regulations</li>
            </ul>
          </section>

          {/* Prohibited Content */}
          <section>
            <h3 className="font-bold text-gray-900 text-base mb-2">3. Prohibited Content</h3>
            <p className="mb-2">The following content is strictly prohibited:</p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Reports about private projects or personal activities unrelated to public services</li>
              <li>Defamatory, false, or misleading information</li>
              <li>Personal attacks, harassment, threats, or abusive language</li>
              <li>Content that violates libel/defamation laws (Republic Act 10175 - Cybercrime Prevention Act)</li>
              <li>Content that makes legal judgments or accusations</li>
              <li>Spam, duplicate reports, or automated submissions</li>
              <li>Content that infringes on intellectual property rights</li>
            </ul>
          </section>

          {/* User Responsibilities */}
          <section>
            <h3 className="font-bold text-gray-900 text-base mb-2">4. User Responsibilities</h3>
            <p className="mb-2">By using this platform, you agree to:</p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Provide accurate and truthful information to the best of your knowledge</li>
              <li>Submit reports documenting observations related to government-funded projects or public services</li>
              <li>Use descriptive, neutral language (avoid accusatory or evaluative statements)</li>
              <li>Respect the rights of others and maintain civil discourse</li>
              <li>Not use the platform for illegal purposes</li>
              <li>Accept responsibility for the content you submit</li>
              <li>Comply with all applicable laws and regulations, including the Data Privacy Act of 2012 (RA 10173)</li>
            </ul>
          </section>

          {/* Content Moderation */}
          <section>
            <h3 className="font-bold text-gray-900 text-base mb-2">5. Content Moderation</h3>
            <p className="mb-2">
              All citizen reports are subject to human-in-the-loop moderation before being published. 
              The platform reserves the right to:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Review, approve, reject, or remove any report</li>
              <li>Remove content that violates these Terms of Service</li>
              <li>Filter content that contains legal judgments, accusations, or evaluative language</li>
              <li>Suspend or terminate accounts that violate these terms</li>
              <li>Take legal action against users who submit false or defamatory content</li>
            </ul>
            <p className="mt-2 text-xs text-gray-600 italic">
              Moderation ensures content aligns with the platform's principle of documenting evidence, 
              not making legal judgments or accusations.
            </p>
          </section>

          {/* Platform Disclaimers */}
          <section>
            <h3 className="font-bold text-gray-900 text-base mb-2">6. Platform Disclaimers</h3>
            <p className="mb-2">
              This platform is a <strong>citizen-generated content portal</strong>. The platform:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Documents citizen-submitted evidence and observations</li>
              <li>Does not determine legality, assign blame, or conduct official audits</li>
              <li>Does not verify the accuracy of user-submitted reports beyond moderation review</li>
              <li>Is not responsible for the content submitted by users</li>
              <li>Does not represent the views of DILG or any government agency</li>
              <li>Does not replace government oversight bodies</li>
              <li>Provides the platform "as is" without warranties</li>
              <li>Reserves the right to modify or discontinue the service at any time</li>
            </ul>
            <p className="mt-2 text-xs text-gray-600 italic">
              All entries are citizen-submitted observations displayed for transparency purposes only.
            </p>
          </section>

          {/* Limitation of Liability */}
          <section>
            <h3 className="font-bold text-gray-900 text-base mb-2">7. Limitation of Liability</h3>
            <p>
              To the maximum extent permitted by law, the platform and its operators shall not be 
              liable for any damages, losses, or legal consequences arising from:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
              <li>User-submitted content</li>
              <li>False or defamatory reports</li>
              <li>Legal actions taken against users</li>
              <li>Service interruptions or technical issues</li>
            </ul>
          </section>

          {/* Intellectual Property */}
          <section>
            <h3 className="font-bold text-gray-900 text-base mb-2">8. Intellectual Property</h3>
            <p className="mb-2">
              By submitting content to this platform, you grant the platform a license to:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Display, publish, and distribute your submitted content</li>
              <li>Use submitted content for platform operations and transparency purposes</li>
              <li>Share anonymized or aggregated data for policy compliance tracking and research purposes</li>
            </ul>
            <p className="mt-2">
              You retain ownership of your submitted content but grant these usage rights.
            </p>
          </section>

          {/* Account Termination */}
          <section>
            <h3 className="font-bold text-gray-900 text-base mb-2">9. Account Termination</h3>
            <p>
              The platform reserves the right to suspend or terminate accounts that violate these 
              Terms of Service, engage in prohibited activities, or submit false or defamatory content. 
              Users may request account deletion at any time.
            </p>
          </section>

          {/* Governing Law */}
          <section>
            <h3 className="font-bold text-gray-900 text-base mb-2">10. Governing Law</h3>
            <p>
              These Terms of Service are governed by the laws of the Republic of the Philippines. 
              Any disputes shall be resolved in accordance with Philippine law and jurisdiction.
            </p>
          </section>

          {/* Updates to Terms */}
          <section>
            <h3 className="font-bold text-gray-900 text-base mb-2">11. Updates to Terms</h3>
            <p>
              The platform reserves the right to update these Terms of Service at any time. 
              Continued use of the platform after changes constitutes acceptance of the updated terms.
            </p>
          </section>

          {/* Contact Information */}
          <section className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-bold text-gray-900 text-base mb-2">12. Contact Information</h3>
            <p className="mb-2">
              For questions about these Terms of Service, please contact the platform administrators 
              through the appropriate channels.
            </p>
            <p className="text-xs text-gray-600">
              <strong>Last Updated:</strong> {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </section>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t flex justify-end">
          <button
            onClick={onClose}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2 rounded-lg transition-colors"
          >
            I Understand
          </button>
        </div>
      </div>
    </div>
  );
};
